import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Car, Compass, Users, Bus, RotateCcw, Sparkles } from 'lucide-react';
import config from 'config';

const API_URL = config.API_URL;

const BUCKETS = [
  { key: 'transfer',          label: 'Transferts',  icon: Car,       accent: '#60A5FA' },
  { key: 'excursion.private', label: 'Privées',     icon: Compass,   accent: '#A78BFA' },
  { key: 'excursion.shared',  label: 'Partagées',   icon: Users,     accent: '#34D399' },
  { key: 'excursion.shuttle', label: 'Navettes',    icon: Bus,       accent: '#FB923C' },
  { key: 'excursion.return',  label: 'Retours',     icon: RotateCcw, accent: '#C084FC' },
];

const PERIOD_OPTIONS = [
  { value: 'month', label: 'Ce mois' },
  { value: 'year',  label: 'Cette année' },
  { value: 'all',   label: 'Tout' },
];

export default function ClientKpiByType() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(false);

  const fetchKpi = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const { data: res } = await axios.get(
        `${API_URL}/api/bookings/my/kpi?period=${period}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchKpi(); }, [fetchKpi]);

  if (!data) {
    if (loading) {
      return (
        <div className="mb-6 p-4 bg-[#1F1F1F] border border-[#C9A24D]/20 rounded-lg text-center text-[#E5E5E5]/50 text-sm" data-testid="client-kpi-loading">
          Chargement de votre tableau de bord…
        </div>
      );
    }
    return null;
  }

  const hasAny = data.totals.total > 0;
  if (!hasAny) return null;

  return (
    <div className="mb-6" data-testid="client-kpi-widget">
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#C9A24D]" />
          <h2 className="text-sm font-medium text-[#E5E5E5]">Votre activité</h2>
        </div>
        <div className="flex items-center gap-1 bg-[#1F1F1F] rounded-full p-0.5 border border-[#C9A24D]/20">
          {PERIOD_OPTIONS.map(o => (
            <button
              key={o.value}
              onClick={() => setPeriod(o.value)}
              data-testid={`period-${o.value}`}
              className={`text-[10px] px-3 py-1 rounded-full transition-colors ${
                period === o.value
                  ? 'bg-[#C9A24D] text-[#0B0B0B] font-semibold'
                  : 'text-[#E5E5E5]/60 hover:text-[#E5E5E5]'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* Global summary */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <SummaryCard label="Total" value={data.totals.total} accent="#C9A24D" testId="total" />
        <SummaryCard label="À venir" value={data.totals.upcoming} accent="#60A5FA" testId="upcoming" />
        <SummaryCard label="Effectuées" value={data.totals.completed} accent="#34D399" testId="completed" />
      </div>

      {/* Buckets */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {BUCKETS.map(({ key, label, icon: Icon, accent }) => {
          const b = data.by_type[key] || { total: 0, completed: 0, upcoming: 0 };
          const dimmed = b.total === 0;
          return (
            <div
              key={key}
              data-testid={`client-bucket-${key}`}
              className={`p-3 rounded-lg bg-[#1F1F1F] border transition-all ${
                dimmed ? 'border-white/5 opacity-40' : 'border-[#C9A24D]/20 hover:border-[#C9A24D]/40'
              }`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
                <span className="text-[10px] text-[#E5E5E5]/60 uppercase tracking-wide">{label}</span>
              </div>
              <div className="text-xl font-bold" style={{ color: accent }}>{b.total}</div>
              {b.total > 0 && (
                <div className="mt-1 flex gap-2 text-[9px] text-[#E5E5E5]/40">
                  {b.upcoming > 0 && <span>À venir: <span className="text-blue-400">{b.upcoming}</span></span>}
                  {b.completed > 0 && <span>Ok: <span className="text-emerald-400">{b.completed}</span></span>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent, testId }) {
  return (
    <div
      className="p-3 rounded-lg bg-gradient-to-br from-[#1F1F1F] to-[#161616] border border-[#C9A24D]/20 text-center"
      data-testid={`client-kpi-summary-${testId}`}
    >
      <div className="text-[10px] text-[#E5E5E5]/50 uppercase tracking-wider mb-1">{label}</div>
      <div className="text-2xl font-bold" style={{ color: accent }}>{value}</div>
    </div>
  );
}
